import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { EmpDetails } from "@aptsi-types";
import { environment } from "@env";
import { Observable } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { ErrorHandlingService } from "../shared/errorHandling/errorHandling.service";
import { API_ENDPOINTS } from "../digital-identity-api-endpoints";

interface GetIdentityProvisioningResponse {
  empdetails: EmpDetails[];
}
interface GetEmpDetailResponse {
  empdetailresponse: string;
}

@Injectable({
  providedIn: "root",
})
export class IdentityProvisioningService {
  private readonly digitalIdUrl = environment.apiUrl;

  constructor(
    private http: HttpClient,
    private errorHandlingService: ErrorHandlingService
  ) {}

  getEmpDetailView(currentPage: number): Observable<EmpDetails[]> {
    return this.http
      .post<GetIdentityProvisioningResponse>(
        `${this.digitalIdUrl}${API_ENDPOINTS.EMPDETAILS.FETCH}`,
        { currentPage }
      )
      .pipe(
        map((response) => response.empdetails),
        catchError((error) => this.errorHandlingService.handleError(error))
      );
  }

  getEmpDetailViewSearch(currentPage: number, searchText: string, filterByHeader: string){
    return this.http
    .post<GetIdentityProvisioningResponse>(
      `${this.digitalIdUrl}${API_ENDPOINTS.EMPDETAILS.EMPDETAILSEARCH}`,
      { currentPage, searchText, filterByHeader }
    )
    .pipe(
      map((response) => response.empdetails),
      catchError((error) => this.errorHandlingService.handleError(error))
    );
  }

  getEmpDetailViewForAssign(currentPage: number): Observable<EmpDetails[]> {
    return this.http
      .post<GetIdentityProvisioningResponse>(
        `${this.digitalIdUrl}${API_ENDPOINTS.EMPDETAILS.FETCHASSIGN}`,
        {currentPage}
      )
      .pipe(
        map((response) => response.empdetails),
        catchError((error) => this.errorHandlingService.handleError(error))
      );
  }

  getEmpDetailViewForAssignSearch( currentPage:number , searchText: string, filterByHeader: string){
    return this.http
    .post<GetIdentityProvisioningResponse>(
      `${this.digitalIdUrl}${API_ENDPOINTS.EMPDETAILS.EMPDETAILASSIGNSEARCH}`,
      { currentPage, searchText, filterByHeader }
    )
    .pipe(
      map((response) => response.empdetails),
      catchError((error) => this.errorHandlingService.handleError(error))
    );
  }

  getAssignedIdEmpDetails(currentPage: number): Observable<EmpDetails[]> {
    return this.http
      .post<GetIdentityProvisioningResponse>(
        `${this.digitalIdUrl}${API_ENDPOINTS.EMPDETAILS.GETS}`,
        { currentPage }
      )
      .pipe(
        map((response) => response.empdetails),
        catchError((error) => this.errorHandlingService.handleError(error))
      );
  }

  getAssignedIdEmpDetailsSearch(currentPage: number, searchText: string,filterByHeader:string){
    return this.http
    .post<GetIdentityProvisioningResponse>(
      `${this.digitalIdUrl}${API_ENDPOINTS.EMPDETAILS.GETASSIGNEDIDEMPDETAILS}`,
      { currentPage, searchText, filterByHeader}
    )
    .pipe(
      map((response) => response.empdetails),
      catchError((error) => this.errorHandlingService.handleError(error))
    );
  }

  mergeEmpDetailView(organizationName: string,organizationUnit:string) {
    console.log("parameters are:");
    return this.http
      .post<GetEmpDetailResponse>(
        `${this.digitalIdUrl}${API_ENDPOINTS.EMPDETAILS.MERGE}`,
        { organizationName, organizationUnit }
      )
      .pipe(
        map((response) => response.empdetailresponse),
        catchError((error) => this.errorHandlingService.handleError(error))
      );
  }

  AssignDigitalId(nullver: number) {
    return this.http
      .post<GetEmpDetailResponse>(
        `${this.digitalIdUrl}${API_ENDPOINTS.EMPDETAILS.ASSIGN}`,
        { nullver }
      )
      .pipe(
        map((response) => response.empdetailresponse),
        catchError((error) => this.errorHandlingService.handleError(error))
      );
  }

  importEmpDetail(organization: string, organizationUnit: string) {
    console.log("parameters are:", organization, organizationUnit);
    return this.http
      .post<GetEmpDetailResponse>(
        `${this.digitalIdUrl}${API_ENDPOINTS.EMPDETAILS.IMPORT}`,
        {
          organization,
          organizationUnit,
        }
      )
      .pipe(
        map((response) => response.empdetailresponse),
        catchError((error) => this.errorHandlingService.handleError(error))
      );
  }

  transferEmpDetail(
    existingdigitalId: string,
    organization: string,
    organizationUnit: string
  ) {
    console.log("parameters are:", organization, organizationUnit);
    return this.http
      .post<GetEmpDetailResponse>(
        `${this.digitalIdUrl}${API_ENDPOINTS.EMPDETAILS.TRANSFEREMP}`,
        {
          existingdigitalId,
          organization,
          organizationUnit,
        }
      )
      .pipe(
        map((response) => response.empdetailresponse),
        catchError((error) => this.errorHandlingService.handleError(error))
      );
  }

  transferOrgUnitDetail(
    currentOrganization: string,
    newOrganization: string,
    organizationUnit: string
  ) {
    return this.http
      .post<GetEmpDetailResponse>(
        `${this.digitalIdUrl}${API_ENDPOINTS.EMPDETAILS.TRANSFERORG}`,
        {
          currentOrganization,
          newOrganization,
          organizationUnit,
        }
      )
      .pipe(
        map((response) => response.empdetailresponse),
        catchError((error) => this.errorHandlingService.handleError(error))
      );
  }

  terminateEmpDetail(status: string, description: string, digitalId: string) {
    return this.http
      .post<GetEmpDetailResponse>(
        `${this.digitalIdUrl}${API_ENDPOINTS.EMPDETAILS.TERMINATE}`,
        {
          status,
          description,
          digitalId,
        }
      )
      .pipe(
        map((response) => response.empdetailresponse),
        catchError((error) => this.errorHandlingService.handleError(error))
      );
  }

  overrideEmpDetails(organization: string) {
    console.log("parameters are:", organization);
    return this.http
      .post<GetEmpDetailResponse>(
        `${this.digitalIdUrl}${API_ENDPOINTS.EMPDETAILS.OVERRIDE}`,
        {
          organization,
        }
      )
      .pipe(
        map((response) => response.empdetailresponse),
        catchError((error) => this.errorHandlingService.handleError(error))
      );
  }

  deltaChangeEmpDetails(organization: string) {
    console.log("parameters are:", organization);
    return this.http
      .post<GetEmpDetailResponse>(
        `${this.digitalIdUrl}${API_ENDPOINTS.EMPDETAILS.DELTACHANGE}`,
        {
          organization,
        }
      )
      .pipe(
        map((response) => response.empdetailresponse),
        catchError((error) => this.errorHandlingService.handleError(error))
      );
  }


   clearData() {
    return this.http
      .post<GetEmpDetailResponse>(
        `${this.digitalIdUrl}${API_ENDPOINTS.EMPDETAILS.CLEARDATA}`,
        {
        }
      )
      .pipe(
        map((response) => response.empdetailresponse),
        catchError((error) => this.errorHandlingService.handleError(error))
      );
    }

}

